import { useEffect, useState } from "react";
import { AlertTriangle, Clock, Sparkles, ThumbsUp, TrendingUp } from "lucide-react";
import { api, getStudentId } from "@/lib/api";
import Navbar from "@/components/Navbar";

export default function ParentDashboard() {
  const studentId = getStudentId();
  const [student, setStudent] = useState(null);
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    api.get(`/students/${studentId}`).then(({ data }) => setStudent(data));
    api.get(`/parent/summary/${studentId}`).then(({ data }) => setSummary(data));
  }, [studentId]);

  if (!summary) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading dashboard...</div>;

  return (
    <div className="min-h-screen bg-background">
      <Navbar student={student} />
      <div className="max-w-5xl mx-auto px-3 sm:px-6 lg:px-8 py-6 sm:py-10">
        <h1 className="text-xl sm:text-2xl font-heading font-extrabold mb-1">Parent Dashboard</h1>
        <p className="text-xs sm:text-sm text-muted-foreground mb-6 sm:mb-8">{student?.name}'s learning summary, this week.</p>

        <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-6 sm:mb-8" data-testid="parent-weekly-summary">
          <div className="bg-card border border-border rounded-xl sm:rounded-2xl p-3 sm:p-5 min-w-0">
            <Clock className="text-primary mb-1.5 sm:mb-2" size={17} />
            <p className="text-base sm:text-2xl font-heading font-extrabold truncate">{summary.learning_time_display}</p>
            <p className="text-[10px] sm:text-xs text-muted-foreground">Learning time</p>
          </div>
          <div className="bg-card border border-border rounded-xl sm:rounded-2xl p-3 sm:p-5 min-w-0">
            <TrendingUp className="text-accent mb-1.5 sm:mb-2" size={17} />
            <p className="text-base sm:text-2xl font-heading font-extrabold truncate">{summary.concepts_studied}</p>
            <p className="text-[10px] sm:text-xs text-muted-foreground">Concepts studied</p>
          </div>
          <div className="bg-card border border-border rounded-xl sm:rounded-2xl p-3 sm:p-5 min-w-0">
            <Sparkles className="text-success mb-1.5 sm:mb-2" size={17} />
            <p className="text-base sm:text-2xl font-heading font-extrabold truncate">{summary.concepts_mastered}</p>
            <p className="text-[10px] sm:text-xs text-muted-foreground">Concepts mastered</p>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
          <div className="bg-card border border-border rounded-xl sm:rounded-2xl p-4 sm:p-5" data-testid="parent-strong-areas">
            <h3 className="font-heading font-bold text-sm mb-3 flex items-center gap-2"><ThumbsUp size={15} className="text-success" /> Strong Areas</h3>
            {summary.strong_areas.length === 0 ? (
              <p className="text-xs text-muted-foreground">No strong areas identified yet.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {summary.strong_areas.map((a) => (<span key={a} className="text-xs font-semibold bg-success/10 text-success px-3 py-1.5 rounded-full">{a}</span>))}
              </div>
            )}
          </div>
          <div className="bg-card border border-border rounded-xl sm:rounded-2xl p-4 sm:p-5" data-testid="parent-needs-attention">
            <h3 className="font-heading font-bold text-sm mb-3 flex items-center gap-2"><AlertTriangle size={15} className="text-secondary" /> Needs Attention</h3>
            {summary.needs_attention_areas.length === 0 ? (
              <p className="text-xs text-muted-foreground">Nothing urgent right now!</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {summary.needs_attention_areas.map((a) => (<span key={a} className="text-xs font-semibold bg-secondary/15 text-secondary-foreground px-3 py-1.5 rounded-full">{a}</span>))}
              </div>
            )}
          </div>
        </div>

        <div className="bg-primary/5 border border-primary/20 rounded-xl sm:rounded-2xl p-4 sm:p-5" data-testid="parent-ai-recommendation">
          <h3 className="font-heading font-bold text-sm mb-2 flex items-center gap-2"><Sparkles size={15} className="text-primary" /> AI Recommendation</h3>
          <p className="text-sm text-muted-foreground">{summary.ai_recommendation}</p>
        </div>
      </div>
    </div>
  );
}
